import { CREATE_TALENT_POOL } from "@lib/graphql/mutations";
import { CAMPAIGNS_QUERY, TALENT_POOLS_QUERY } from "@lib/graphql/queries";
import { useChatDataVersion } from "@lib/hooks/useChatDataVersion";
import { type Connection, usePaginatedList } from "@lib/hooks/usePaginatedList";
import { Plus } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { useMutation, useQuery } from "urql";

interface Campaign {
  id: string;
  title: string;
  status: string;
}

interface TalentPool {
  id: string;
  name: string;
  kind: string;
  description: string | null;
  candidateCount: number;
  campaign: Campaign | null;
  createdBy: { id: string; name: string };
  createdAt: string;
}

export function TalentPoolList() {
  const [onlyStandalone, setOnlyStandalone] = useState(false);
  const {
    nodes: pools,
    fetching,
    hasNextPage,
    loadMore,
    refresh,
  } = usePaginatedList<{ talentPools: Connection<TalentPool> }, TalentPool>({
    query: TALENT_POOLS_QUERY,
    variables: { onlyStandalone },
    getConnection: (data) => data.talentPools,
  });
  // Campaign picker: one large page (field max_page_size 250), not paginated.
  const [{ data: campaignsData }] = useQuery({
    query: CAMPAIGNS_QUERY,
    variables: { first: 250 },
  });
  const [, createTalentPool] = useMutation(CREATE_TALENT_POOL);
  useChatDataVersion(refresh);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [campaignId, setCampaignId] = useState("");

  const campaigns: Campaign[] = (campaignsData?.campaigns?.edges ?? []).map(
    (edge: { node: Campaign }) => edge.node,
  );

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    const result = await createTalentPool({
      input: { name, description: description || undefined, campaignId: campaignId || undefined },
    });
    if (result.data?.createTalentPool?.errors?.length === 0) {
      setShowForm(false);
      setName("");
      setDescription("");
      setCampaignId("");
      refresh();
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-text-primary">Talent Pools</h1>
          <p className="mt-1 text-sm text-text-muted">
            Organise candidates into reusable talent pools
          </p>
        </div>
        <div className="flex items-center gap-4">
          <label className="flex cursor-pointer items-center gap-2 text-xs text-text-muted">
            <input
              type="checkbox"
              checked={onlyStandalone}
              onChange={(e) => setOnlyStandalone(e.target.checked)}
              className="h-4 w-4 cursor-pointer rounded border-border accent-accent"
            />
            <span>Hide campaign pools</span>
          </label>
          <button
            onClick={() => setShowForm(!showForm)}
            className="flex items-center gap-2 rounded-md bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-hover"
          >
            <Plus size={16} /> New Pool
          </button>
        </div>
      </div>

      {showForm && (
        <form
          onSubmit={handleCreate}
          className="mt-4 rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring"
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label className="block text-sm font-medium text-text-secondary">Name *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="mt-1 block w-full rounded-md border border-input-border bg-card px-3 py-2 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-secondary">Description</label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="mt-1 block w-full rounded-md border border-input-border bg-card px-3 py-2 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-secondary">
                Campaign (optional)
              </label>
              <select
                value={campaignId}
                onChange={(e) => setCampaignId(e.target.value)}
                className="mt-1 block w-full rounded-md border border-input-border bg-card px-3 py-2 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
              >
                <option value="">None</option>
                {campaigns.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.title} ({c.status})
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <button
              type="submit"
              className="rounded-md bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-hover"
            >
              Save
            </button>
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="rounded-md border border-input-border px-4 py-2 text-sm font-medium text-text-secondary hover:bg-card-hover"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {fetching && pools.length === 0 ? (
        <p className="mt-4 text-sm text-text-muted">Loading...</p>
      ) : pools.length === 0 ? (
        <div className="mt-4 rounded-lg border-2 border-dashed border-border-dashed p-8 text-center">
          <p className="text-sm text-text-muted">
            No talent pools yet. Create your first pool to start organising candidates.
          </p>
        </div>
      ) : (
        <div className="mt-4 overflow-hidden rounded-lg bg-card shadow-sm ring-1 ring-ring">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-card-alt">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted">
                  Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted">
                  Description
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted">
                  Type
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted">
                  Candidates
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted">
                  Created By
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {pools.map((pool) => (
                <tr key={pool.id} className="hover:bg-card-hover">
                  <td className="px-4 py-3">
                    <Link
                      to={
                        pool.kind === "campaign_pool" && pool.campaign
                          ? `/campaigns/${pool.campaign.id}/pool`
                          : `/talent-pools/${pool.id}`
                      }
                      className="font-medium text-accent hover:text-accent-hover"
                    >
                      {pool.name}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-sm text-text-secondary">
                    {pool.description || "\u2014"}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {pool.kind === "campaign_pool" && pool.campaign ? (
                      <div>
                        <span className="inline-flex items-center rounded-full bg-accent/10 px-2 py-0.5 text-xs font-medium text-accent">
                          Campaign Pool
                        </span>
                        <Link
                          to={`/campaigns/${pool.campaign.id}`}
                          className="mt-1 block text-xs text-accent hover:text-accent-hover"
                        >
                          {pool.campaign.title}
                        </Link>
                      </div>
                    ) : (
                      <span className="inline-flex items-center rounded-full bg-text-muted/10 px-2 py-0.5 text-xs font-medium text-text-muted">
                        Standalone Pool
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-text-secondary">{pool.candidateCount}</td>
                  <td className="px-4 py-3 text-sm text-text-secondary">{pool.createdBy.name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {hasNextPage && (
        <div className="mt-4 flex justify-center">
          <button
            onClick={loadMore}
            disabled={fetching}
            className="rounded-md border border-input-border bg-card px-4 py-2 text-sm font-medium text-text-secondary hover:bg-card-hover disabled:opacity-50"
          >
            {fetching ? "Loading..." : "Load more"}
          </button>
        </div>
      )}
    </div>
  );
}
